import './styles.css';
import { GameUi } from './ui/GameUi';
import { CrowdAudio } from './systems/CrowdAudio';
import { buildScorecard } from './ui/scorecard';
import type { HoopsGame } from './game/HoopsGame';

const canvas = document.querySelector<HTMLCanvasElement>('#game-canvas');
if (!canvas) throw new Error('Missing #game-canvas element.');

let game: HoopsGame | null = null;
const audio = new CrowdAudio();
/** Signed by the server when a run starts; a claim without it is refused. */
let playToken: string | null = null;

const ui = new GameUi({
  onStart: () => {
    // Must happen inside the gesture itself: an AudioContext created after an await
    // is born suspended on Safari and never recovers without another tap.
    audio.unlock();
    void boot();
  },
  onRestart: () => {
    audio.unlock();
    game?.startRun();
    void refreshToken();
  },
  onClaim: (email: string, makes: number, kind: 'win' | 'card') => claim(email, makes, kind),
});

/**
 * The physics engine and the arena only load once the player asks to play, so the
 * landing page itself stays light.
 */
async function boot(): Promise<void> {
  if (!game) {
    const { HoopsGame: Hoops } = await import('./game/HoopsGame');
    game = new Hoops(canvas!, ui, audio);
    await game.init();
  }
  game.startRun();
  void refreshToken();
}

/** One token per run. Failing to get one is not fatal until a claim is attempted. */
async function refreshToken(): Promise<void> {
  try {
    const response = await fetch('/api/token', { cache: 'no-store' });
    if (!response.ok) return;
    const body = (await response.json()) as { token?: string };
    playToken = typeof body.token === 'string' ? body.token : null;
  } catch {
    playToken = null;
  }
}

async function claim(email: string, makes: number, kind: 'win' | 'card'): Promise<void> {
  const code = runCode();
  const response = await fetch('/api/claim', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, makes, kind, code, token: playToken, company: '' }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? 'Could not send that just now. Try again in a moment.');
  }
  // The card is handed over only after the DLSM Team has been told to expect it,
  // so the code on the download always matches the code in their inbox.
  if (kind === 'card') {
    ui.setCardReference(code);
    await downloadScorecard(makes, code);
  }
}

/** Short reference shared by the scorecard and the email about it. */
function runCode(): string {
  const source = (playToken ?? String(Date.now())).replace(/[^A-Za-z0-9]/g, '');
  return `HOOPS-${source.slice(-6).toUpperCase()}`;
}

// QA hook: lets a headless browser render a real card and look at it.
(window as unknown as { __BUILD_SCORECARD__?: unknown }).__BUILD_SCORECARD__ = buildScorecard;

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    game?.dispose();
    game = null;
  });
}

/** Loss path: draw the card, hand it to the browser, and let the player email it. */
async function downloadScorecard(makes: number, code: string): Promise<void> {
  const blob = await buildScorecard({ makes, target: 5, code, logoUrl: '/dlsm-logo.png' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `dlsm-hoops-${makes}-of-5.png`;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoking immediately can cancel the save on some mobile browsers.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
